import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

export const RM_DOC_TYPES = [
  { key: "legal", label: "Certificate of registration / incorporation" },
  { key: "cr12_business_permit", label: "CR12 or business permit" },
  { key: "tax_compliance", label: "KRA PIN & tax compliance certificate" },
  { key: "pitch_deck", label: "Pitch deck" },
  { key: "financial_model", label: "Financial model / projections" },
  { key: "mgmt_accounts", label: "Management accounts" },
  { key: "audited_accounts", label: "Audited financial statements" },
  { key: "business_valuation", label: "Business valuation" },
  { key: "bank_statements", label: "Bank or M-Pesa statements" },
  { key: "business_plan", label: "Business plan" },
];
export const RM_DOC_LABEL = Object.fromEntries(RM_DOC_TYPES.map((d) => [d.key, d.label]));

export const ClientDocumentUpload = ({ companyId, clientName, onUploaded }) => {
  const [docType, setDocType] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!docType) { toast.error("Choose the document type first"); return; }
    if (!file) { toast.error("Choose a file to upload"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const { data } = await api.post(`/rm/clients/${companyId}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.client_notified
        ? `Uploaded — ${clientName || "the client"} has been emailed`
        : "Uploaded to the client's profile");
      setFile(null); setDocType("");
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-xl border border-dashed border-[#0A8B85]/40 bg-[#0A8B85]/5 p-3" data-testid={`rm-doc-upload-${companyId}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A8B85]">Upload on behalf of the client</p>
      <p className="mt-0.5 text-[11px] text-slate-500">
        The file is saved to the client&apos;s Data Room exactly as if they uploaded it, logged under your name, and the client is emailed.
      </p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} disabled={busy}
          className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-[#0A8B85] sm:w-64"
          data-testid={`rm-doc-upload-type-${companyId}`}>
          <option value="">Document type…</option>
          {RM_DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
        <input ref={inputRef} type="file" disabled={busy}
          accept=".pdf,.xlsx,.xls,.pptx,.ppt,.docx,.doc,.csv,.png,.jpg,.jpeg"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="flex-1 text-xs text-slate-600 file:mr-2 file:rounded-lg file:border-0 file:bg-white file:px-2.5 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700"
          data-testid={`rm-doc-upload-file-${companyId}`} />
        <button type="button" onClick={submit} disabled={busy || !file || !docType}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#097a74] disabled:opacity-40"
          data-testid={`rm-doc-upload-submit-${companyId}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? "Uploading…" : "Upload"}
        </button>
      </div>
    </div>
  );
};
